import {useEffect, useState} from "react";
import {
  ensureGamanzaEngage,
  refreshGamanzaWidgetsAfterPaint,
} from "@/services/gamanza/gamanzaEngageSdk";
import {useThemeStore} from "@/store/useTheme.store";

const DEFAULT_MAX_WAIT_MS = 6000;

const isAvatarMount = node => {
  if (!node) return false;
  if (node.getAttribute?.("data-type") === "avatar") return true;
  return Boolean(node.querySelector?.('.gamification_widget[data-type="avatar"], [data-type="avatar"]'));
};

/** True when an <img> has finished loading with real pixels (rank or assets.avatar_image). */
const hasLoadedAvatarImage = node => {
  if (!node) return false;
  const imgs = node.querySelectorAll("img");
  if (!imgs.length) return false;
  return Array.from(imgs).some(img => {
    const src = (img.currentSrc || img.getAttribute("src") || "").trim();
    if (!src) return false;
    return img.complete && img.naturalWidth > 0;
  });
};

const nodeHasWidgetContent = node => {
  if (!node) return false;

  // Avatars: keep skeleton until real image (not the default user SVG) is painted
  if (isAvatarMount(node)) {
    return hasLoadedAvatarImage(node);
  }

  if (node.childElementCount > 0) return true;
  return Boolean(node.querySelector("img, canvas, svg, iframe, [class]"));
};

/**
 * Ensures Engage is ready and tracks when target .gamification_widget nodes get content.
 * Keeps widgets mounted (for Gamanza to fill) while callers show a skeleton overlay.
 */
export const useGamanzaWidgetHydration = ({
  enabled,
  token,
  rootRef = null,
  widgetId = null,
  maxWaitMs = DEFAULT_MAX_WAIT_MS,
} = {}) => {
  const theme = useThemeStore(state => state.theme);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!enabled || !token) {
      setHydrated(false);
      return undefined;
    }

    let cancelled = false;
    let observer = null;
    let timeoutId = null;
    let pollId = null;
    const imgListeners = [];

    setHydrated(false);

    const getTargets = () => {
      if (widgetId) {
        const byId = document.getElementById(widgetId);
        if (byId) return [byId];
      }
      const root = rootRef?.current;
      if (root) {
        if (root.classList?.contains("gamification_widget")) return [root];
        return Array.from(root.querySelectorAll(".gamification_widget"));
      }
      return [];
    };

    const checkReady = () => {
      const targets = getTargets();
      if (targets.length === 0) return false;
      return targets.every(nodeHasWidgetContent);
    };

    const markReady = () => {
      if (cancelled) return;
      setHydrated(true);
      if (observer) observer.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
      if (pollId) window.clearInterval(pollId);
      imgListeners.forEach(({img, onLoad, onError}) => {
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onError);
      });
      imgListeners.length = 0;
    };

    const bindImageLoads = () => {
      getTargets().forEach(target => {
        target.querySelectorAll("img").forEach(img => {
          if (img.dataset.gamanzaHydrationBound === "1") return;
          img.dataset.gamanzaHydrationBound = "1";
          const onLoad = () => {
            if (checkReady()) markReady();
          };
          const onError = () => {
            // Still allow timeout fallback; don't mark ready on broken src alone
          };
          img.addEventListener("load", onLoad);
          img.addEventListener("error", onError);
          imgListeners.push({img, onLoad, onError});
          if (img.complete && img.naturalWidth > 0 && checkReady()) {
            markReady();
          }
        });
      });
    };

    const startWatching = () => {
      if (checkReady()) {
        markReady();
        return;
      }

      const targets = getTargets();
      if (targets.length && typeof MutationObserver !== "undefined") {
        observer = new MutationObserver(() => {
          bindImageLoads();
          if (checkReady()) markReady();
        });
        targets.forEach(node => {
          observer.observe(node, {childList: true, subtree: true, attributes: true});
        });
      }

      bindImageLoads();

      pollId = window.setInterval(() => {
        bindImageLoads();
        if (checkReady()) markReady();
      }, 200);

      timeoutId = window.setTimeout(markReady, maxWaitMs);
    };

    void ensureGamanzaEngage(token)
      .then(() => {
        if (cancelled) return;
        refreshGamanzaWidgetsAfterPaint(widgetId);
        window.setTimeout(() => {
          if (cancelled) return;
          refreshGamanzaWidgetsAfterPaint(widgetId);
          startWatching();
        }, 50);
      })
      .catch(() => {
        markReady();
      });

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
      if (pollId) window.clearInterval(pollId);
      imgListeners.forEach(({img, onLoad, onError}) => {
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onError);
      });
    };
  }, [enabled, token, theme, rootRef, widgetId, maxWaitMs]);

  return {
    isHydrating: Boolean(enabled && !hydrated),
    isReady: hydrated,
  };
};

export default useGamanzaWidgetHydration;
